import { useEffect, useMemo, useState } from 'react';
import { useAppContext } from 'src/components/context/AppContextProvider';
import { LoadingStatus } from 'src/components/context/AppContextModels';
import { fetchFinanceCalendarEvents } from 'src/api/app-sync-services';
import { FinanceCalendarEventEntity } from 'src/api/interfaces';
import { StatusType } from 'src/components/fintech-ops/sas/SasInterfaces';
import { FinanceCalendarDataTypes, toFinanceCalendarWidgetData } from '../utils/financeCalendarTransformers';

interface DueThisWeekData {
  financeCalendarData: FinanceCalendarDataTypes[];
  calendarStatus: StatusType;
}

const CACHE_KEY = 'finance-calendar-events';
const CACHE_DATE_KEY = 'finance-calendar-events-date';

export const useDueThisWeekData = (): DueThisWeekData => {
  const appContext = useAppContext();
  const [calendarEvents, setCalendarEvents] = useState<FinanceCalendarEventEntity[]>([]);
  const [calendarStatus, setCalendarStatus] = useState<StatusType>('loading');

  useEffect(() => {
    if (appContext.contextLoadingStatus !== LoadingStatus.Completed) return;
    let cancelled = false;
    let hasCachedData = false;

    const today = new Date().toISOString().slice(0, 10);
    const cachedDate = sessionStorage.getItem(CACHE_DATE_KEY);
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached && cachedDate === today) {
      try {
        setCalendarEvents(JSON.parse(cached));
        setCalendarStatus('finished');
        hasCachedData = true;
      } catch {
        /* fall through to fetch */
      }
    }

    fetchFinanceCalendarEvents()
      .then((events) => {
        if (!cancelled) {
          setCalendarEvents(events);
          setCalendarStatus('finished');
          try {
            sessionStorage.setItem(CACHE_KEY, JSON.stringify(events));
            sessionStorage.setItem(CACHE_DATE_KEY, new Date().toISOString().slice(0, 10));
          } catch {
            /* ignore cache write failure */
          }
        }
      })
      .catch(() => {
        if (!cancelled && !hasCachedData) setCalendarStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [appContext.contextLoadingStatus]);

  const financeCalendarData = useMemo(() => toFinanceCalendarWidgetData(calendarEvents), [calendarEvents]);

  return { financeCalendarData, calendarStatus };
};
